import { Injectable } from '@angular/core';
import { ApiErrors, ApiErrorsMessage } from '../enums';
import { IErrorDefinition } from '../interfaces/error-definition.interface';
import { AgencyDatePipe } from '../pipes/agency-date.pipe';

@Injectable()
export class ErrorMessagesService {
  constructor(private readonly agencyDatePipe: AgencyDatePipe) {}

  public getMessage(error: IErrorDefinition, inputName?: string): string {
    switch (error.key) {
      case 'required':
      case 'requiredError':
        return this.getRequiredError(inputName);
      case 'minlength':
        return this.getMinLengthError(error.value.requiredLength, inputName);
      case 'maxlength':
        return this.getMaxLengthError(error.value.requiredLength, inputName);
      case 'isNotNumber':
        return this.getIsNotNumberError(inputName);
      case 'isTooBig':
        return this.getIsTooBigError(inputName, error.value.params);
      case 'isTooSmall':
        return this.getIsTooSmallError(inputName, error.value.params);
      case 'hasRedundantDecimals':
        return this.getHasRedundantDecimalsError(inputName, error.value.params);
      case 'arrayContains':
        return error.value.params.message;
      case 'invalidEmail':
        return this.getInvalidEmailError();
      case 'isInvalidPasswordLength':
        return this.getIsLenghtInvalidPasswordError();
      case 'isPasswordMissingSpecialCharacters':
        return this.getIsHasSpecialCharactersPasswordError();
      case 'isPasswordMissingAlphanumericCharacters':
        return this.getIsHasAlphanumericCharactersPasswordError();
      case 'isNotConfirmedPassword':
        return this.getIsNotConfirmedPasswordError();
      case 'owlDateTimeParse':
        return this.getOwlDateError();
      case 'isInvalidDate':
        return this.getOwlDateError();
      case 'noHtmlTags':
        return this.getNoHtmlTags();
      case 'noSymbols':
        return this.getNoSymbols();
      case 'customError':
        return error.value;
      case 'customMessage':
        return error.value;
      case 'minDate':
        return this.getMinDateError(inputName, error.value);
      case 'maxDate':
        return this.getMaxDateError(inputName, error.value);
      case 'claimField':
        return this.getClaimFieldError();
      case 'dateRangeRequiredError':
        return this.getDateRangeRequiredError();
      case 'dateRangeError':
        return this.getDateRangeError();
      case 'dateShouldBeAfter':
        return this.getDateShouldBeAfterError();
      case 'usedDateRangeError':
        return this.getUsedDateRangeError();
      case 'instanceNumberTaken':
        return this.getInstanceNumberError(error.value.params);
      case 'guid':
        return this.getGuidError();
      case 'duplicate':
        return this.getDuplicateError(inputName);
      case 'isNegative':
        return this.getNegativeError(inputName);
      case 'invalidSiteKey':
        return this.getInvalidSiteKeyError();
      case ApiErrors.UnsafePassword:
        return ApiErrorsMessage[ApiErrors.UnsafePassword];
      default:
        return 'General error';
    }
  }

  private getInstanceNumberError(params: any): string {
    return `This phase already has a ${params.formInstanceName} with that number. Please choose another.`;
  }

  private getDateRangeRequiredError(): string {
    return 'Both dates are required.';
  }

  private getUsedDateRangeError(): string {
    return 'The dates are already in use.';
  }

  private getDateRangeError(): string {
    return 'The date range is incorrect.';
  }

  private getDateShouldBeAfterError(): string {
    return 'Date must be greater than Start Date.';
  }

  private getRequiredError(inputName?: string): string {
    return `${inputName || 'Value'} cannot be empty.`;
  }

  private getMinLengthError(length: number, inputName?: string): string {
    return `${inputName || 'Value'} must be at least ${length} characters in length.`;
  }

  private getMaxLengthError(length: number, inputName?: string): string {
    return `${inputName || 'Value'} must be no more than ${length} characters in length.`;
  }

  private getIsNotNumberError(inputName?: string): string {
    return `${inputName || 'Value'} should contain valid number.`;
  }

  private getIsTooBigError(inputName: string, params: any): string {
    return `${inputName || 'Value'} should not exceed ${params.max}.`;
  }

  private getIsTooSmallError(inputName: string, params: any): string {
    return `${inputName || 'Value'} should be no less than ${params.min}.`;
  }

  private getHasRedundantDecimalsError(inputName: string, params: any): string {
    return `${inputName || 'Value'} must have no more ${params.decimals} decimals.`;
  }

  private getInvalidEmailError(): string {
    return 'This field must be a valid email address.';
  }

  private getIsLenghtInvalidPasswordError(): string {
    return 'Your password must contain at least 12 characters';
  }

  private getIsHasSpecialCharactersPasswordError(): string {
    return 'Password must include special characters';
  }

  private getIsHasAlphanumericCharactersPasswordError(): string {
    return 'Password must include letters or numbers';
  }

  private getClaimFieldError(): string {
    return 'Characters combination |; denied on this input.';
  }

  private getIsNotConfirmedPasswordError(): string {
    return 'Passwords are not the same.';
  }

  private getOwlDateError(): string {
    return 'Invalid date format.';
  }

  private getNoHtmlTags(): string {
    return 'Invalid input. No html tags.';
  }

  private getNoSymbols(): string {
    return 'Invalid input. No symbols or leading/trailing whitespaces.';
  }

  private getMinDateError(inputName: string, date: Date): string {
    return `${inputName || 'Value'} must be no earlier than ${this.agencyDatePipe.transform(date)}.`;
  }

  private getMaxDateError(inputName: string, date: Date): string {
    return `${inputName || 'Value'} must be no later than ${this.agencyDatePipe.transform(date)}.`;
  }

  private getGuidError(): string {
    return 'This field must be a valid GUID.';
  }
  private getDuplicateError(inputName: string): string {
    return `${inputName || 'Value'} already exists in the system. Please type another one.`;
  }
  private getNegativeError(inputName: string): string {
    return `${inputName || 'Value'} must be non-negative.`;
  }
  private getInvalidSiteKeyError(): string {
    return 'Invalid site key.';
  }
}
